import { useDispatch } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  CLIENTS_RELATIONSHIP_ADMIN_ACCESS,
  ROLE_CLIENT_ADMIN,
  ROLE_SUPER_ADMIN,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW_TAB,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { useLocation, useNavigate, useParams } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useEffect, useRef, useState } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { ClientRelationshipReview } from "../types/clients-module-types";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { useClientRelationshipReviewTab } from "../hooks/useClientRelationshipReviewsTab";
import { setShowActivesInTab } from "../redux/reducers/clientRelationshipReviewsList.reducer";
import { TbEdit } from "react-icons/tb";
import { MdDoNotDisturb, MdFileDownloadDone } from "react-icons/md";
import { ModalPortal } from "../../SharedModule/components/ModalPortal";
import { ModalAddEditRelationshipReview } from "../components/ModalAddEditRelationshipReview";
import { useChangeStatusRelationship } from "../hooks/useChangeStatusRelationship";
import { useModal } from "../../SharedModule/hooks/useModal";
import eventHandler from "../../SharedModule/utils/eventHandler";
import { setNeedUpdateKeyContactList } from "../redux/reducers/clientKeyContacts.reducer";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientsRelationshipReviewsTabPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    ClientForHeaderInfo,
    userRolesList,
    isLoadingReviews,
    isApplySupervisorCheck,
    costCenterOwnedIds,
    rolesLoaded,
    clientRelationshipReview,
    isShowingActivesInTab,
    loadRelationshipReview,
    setIsLoadingReviews,
    isClientsLoading,
    clients,
    isLoadingAllCostCenters,
    allCostCenters,
  } = useClientRelationshipReviewTab(Number(id));

  useEffect(() => {
    document.title = "The Hub - Client Relationship Reviews";

    dispatch(setActiveTab(ROUTE_CLIENTS_RELATIONSHIP_REVIEW_TAB));

    eventHandler.on("toast action", (data) => {
      if (data.isClosed) {
        setIsToastClosed(true);
      } else {
        setIsToastClosed(false);
      }
    });

    return () => {
      resetScrollY(globalThis.location.pathname);
      eventHandler.remove("toast action", null);
      if (
        !location.pathname.endsWith("/relationshipReviews") ||
        !(
          globalThis.location.pathname.endsWith("/keyContacts") ||
          globalThis.location.pathname.endsWith("/relationshipReviews")
        ) ||
        ROUTE_CLIENTS_RELATIONSHIP_REVIEW === globalThis.location.pathname
      ) {
        setSeeActiveRelationships(true);
        dispatch(setShowActivesInTab(true));
      }
    };
  }, []);

  const [editMode, setEditMode] = useState(true);
  const [reviewToEdit, setReviewToEdit] = useState<any>(null);
  const [seeActiveRelationships, setSeeActiveRelationships] = useState(
    isShowingActivesInTab
  );
  const [isToastClosed, setIsToastClosed] = useState(true);

  const { showModal, closeModal, Modal, ...modalProps } = useModal();
  const changeStatusRelationship = useChangeStatusRelationship(
    showModal,
    closeModal,
    seeActiveRelationships,
    setSeeActiveRelationships,
    setIsLoadingReviews
  );

  // To show better message if there is not rel review
  let relationshipReviewByClient: Array<ClientRelationshipReview> =
    clientRelationshipReview.filter((review: ClientRelationshipReview) => {
      return review.clientId === Number(id);
    });

  let relationshipReviewToShow: Array<ClientRelationshipReview> =
    relationshipReviewByClient.filter((review: ClientRelationshipReview) => {
      return seeActiveRelationships ? review.isActive : !review.isActive;
    });

  const onToggleButton = (state: boolean) => {
    setSeeActiveRelationships(state);
    dispatch(setShowActivesInTab(!isShowingActivesInTab));
  };

  const goToKeyContactsList = (review: ClientRelationshipReview) => {
    navigate(`/clients/${id}/relationshipReviews/${review.id}/keyContacts`, {
      state: {
        from:
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            : "",
      },
    });
  };

  // Modal config
  const modalRef = useRef<undefined>(undefined);
  const showAddModal = () => {
    (modalRef.current as any).show(true);
  };
  const closeAddModal = (refresh: boolean, isCreate: boolean) => {
    (modalRef.current as any).show(false);
    if (isCreate) setSeeActiveRelationships(true);
    if (refresh) {
      dispatch(setNeedUpdateKeyContactList(true));
      loadRelationshipReview(true);
    }
  };

  const addRelationshipReview = () => {
    setEditMode(false);
    setReviewToEdit(null);
    showAddModal();
  };

  const handleEdit = (event: any, review: ClientRelationshipReview) => {
    event.stopPropagation();
    setEditMode(true);
    setReviewToEdit(review);
    showAddModal();
  };

  const handleReview = async (event: any, review: ClientRelationshipReview) => {
    event.stopPropagation();
    // Return a boolean value if it's in inactive view, we should set ViewInactives to false because we reload products list
    await changeStatusRelationship({ review: review });
    dispatch(setNeedUpdateKeyContactList(true));
    loadRelationshipReview(true);
  };

  const isAbleToAddRelationship = () => {
    let idsRelationshipInPage = relationshipReviewByClient.map(
      (rr) => rr.costCenterId
    );
    return (
      ClientForHeaderInfo?.isActive &&
      (verifyModuleAccess(
        true,
        [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
        userRolesList
      ) ||
        (isApplySupervisorCheck &&
          idsRelationshipInPage.filter((id) => costCenterOwnedIds.includes(id))
            .length > 0))
    );
  };

  return (
    <>
      <Modal closeModal={closeModal} {...modalProps} />
      <ModalPortal ref={modalRef}>
        <ModalAddEditRelationshipReview
          btnCancel={closeAddModal}
          actionPlaceholder="notCloseOnClickOutside"
          clients={clients}
          isClientsLoading={isClientsLoading}
          costCenters={allCostCenters}
          isLoadingAllCostCenters={isLoadingAllCostCenters}
          editMode={editMode}
          isClientTabPage={true}
          reviewToEdit={reviewToEdit}
          clientId={Number(id)}
          costCenterOwnedIds={
            isApplySupervisorCheck ? costCenterOwnedIds : null
          }
        />
      </ModalPortal>
      <div className="content">
        <HeaderClientsModule
          isLoadingPage={!rolesLoaded || isLoadingReviews || !isToastClosed}
          backFromRelationshipPage={
            location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
          }
        />

        {(!rolesLoaded || isLoadingReviews || !isToastClosed) && (
          <Spinner
            style={{
              marginLeft: "50%",
              marginTop: "5%",
            }}
          />
        )}

        {!isLoadingReviews &&
          isToastClosed &&
          rolesLoaded &&
          !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
            <ForbiddenPage />
          )}

        {!isLoadingReviews &&
          isToastClosed &&
          rolesLoaded &&
          verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
            <>
              <fieldset className="d-flex w-100 my-4 justify-content-end">
                {relationshipReviewByClient.length > 0 && (
                  <div className="btn-group">
                    <button
                      type="button"
                      onClick={() => onToggleButton(true)}
                      className={`btn btn-group-custom ${
                        !seeActiveRelationships ? "btn-light" : "btn-active"
                      }`}
                      disabled={
                        seeActiveRelationships ||
                        isLoadingReviews ||
                        !isToastClosed
                      }
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleButton(false)}
                      className={`btn btn-group-custom ${
                        !seeActiveRelationships ? "btn-inactive" : "btn-light"
                      }`}
                      disabled={
                        !seeActiveRelationships ||
                        isLoadingReviews ||
                        !isToastClosed
                      }
                    >
                      Inactive
                    </button>
                  </div>
                )}

                {isAbleToAddRelationship() && (
                  <div className="ms-5">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => addRelationshipReview()}
                      style={{ minWidth: "175px" }}
                      disabled={!isToastClosed}
                    >
                      Add Relationship Review
                    </button>
                  </div>
                )}
              </fieldset>

              {relationshipReviewByClient.length === 0 && (
                <div className="py-5">
                  <NoContent
                    title="No Relationship Reviews for this Client"
                    image={image}
                    style={{ margin: "0 auto" }}
                  />
                </div>
              )}

              {relationshipReviewByClient.length > 0 &&
                relationshipReviewToShow.length === 0 && (
                  <div className="py-5">
                    <NoContent
                      title="No Results"
                      text="Edit your filters to find Relationship Reviews you are looking for."
                      image={image}
                      style={{ margin: "0 auto" }}
                    />
                  </div>
                )}

              {relationshipReviewToShow.length > 0 && (
                <table
                  className="table grey-table-header-background mb-7"
                  style={{ width: "100%" }}
                >
                  <thead className="align-middle sticky sticky-header">
                    <tr>
                      <th
                        className="border-dark border-top fw-600 ps-5 column-55 pointer"
                        id="name"
                      >
                        Name
                      </th>
                      <th
                        className="border-dark border-top fw-600 ps-5 column-25 pointer"
                        id="costCenter"
                      >
                        Cost Center
                      </th>
                      <th className="border-dark border-top column-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {relationshipReviewToShow.map(
                      (review: ClientRelationshipReview) => {
                        return (
                          <tr
                            key={review.id}
                            onClick={() => goToKeyContactsList(review)}
                            className="product-link"
                          >
                            <td className="ps-5 fw-500 td-shadow-effect">
                              {review.name}
                            </td>
                            <td className="ps-5 fw-500 td-shadow-effect">
                              {review.costCenterId} - {review.costCenterName}{" "}
                              {allCostCenters.some(
                                (cc) => cc.value === review.costCenterId
                              )
                                ? ""
                                : "(x)"}
                            </td>
                            <td className="td-shadow-effect">
                              {verifyModuleAccess(
                                true,
                                CLIENTS_RELATIONSHIP_ADMIN_ACCESS,
                                userRolesList
                              ) &&
                                ClientForHeaderInfo?.isActive &&
                                ((isApplySupervisorCheck &&
                                  costCenterOwnedIds.includes(
                                    review.costCenterId
                                  )) ||
                                  !isApplySupervisorCheck) && (
                                  <>
                                    {review.isActive && (
                                      <TbEdit
                                        size={"20px"}
                                        style={{ margin: "0 15px 5px 0" }}
                                        className="purpleHover iconsRow"
                                        onClick={(e) => handleEdit(e, review)}
                                      />
                                    )}
                                    {seeActiveRelationships &&
                                      review.isActive && (
                                        <MdDoNotDisturb
                                          size={"20px"}
                                          style={{ margin: "0 15px 5px 0" }}
                                          className="redHover iconsRow"
                                          onClick={(e) =>
                                            handleReview(e, review)
                                          }
                                        />
                                      )}
                                    {!seeActiveRelationships &&
                                      !review.isActive && (
                                        <MdFileDownloadDone
                                          size={"20px"}
                                          style={{ margin: "0 15px 5px 0" }}
                                          className="purpleHover iconsRow"
                                          onClick={(e) =>
                                            handleReview(e, review)
                                          }
                                        />
                                      )}
                                  </>
                                )}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        <Footer />
      </div>
    </>
  );
};
